/**
 * iPIDiagram Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { APIResponse } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, LOAD_TYPES, RESPONSE_DISPLAY_CONFIG } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, ResponseDisplay, GenerateLinkButton } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'PI Diagram',
          length: '10',
          tribWidth: '5',
          hingeLength: '5',
          totalWeight: '5*10*12',
          klmFactor: '0.72',
          Ru: '9',
          Xe: '1.0',
          loadType: '0',
          Pa: '1, 4, 10',
          Ia: '10, 28, 89',
          ductilityLimit: '1, 3, 12, 25',
          rotationLimit: '3, 3, 10, 20'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, APIResponse>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, APIResponse>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, APIResponse>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });
  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    { kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Length' },
    { kind: 'input', name: 'tribWidth', label: 'Tributary Width', unit: 'ft', helpText: 'Enter the tributary width', placeholder: 'Tributary Width' },
    { kind: 'input', name: 'hingeLength', label: 'Hinge Length', unit: 'ft', helpText: 'Enter the hinge length', placeholder: 'Hinge Length' },
    { kind: 'input', name: 'totalWeight', label: 'Total Weight', unit: 'lbs', helpText: 'Enter the total weight', placeholder: 'Total Weight' },
    { kind: 'input', name: 'klmFactor', label: 'KLM Factor', unit: '', helpText: 'Enter the KLM factor', placeholder: 'KLM Factor' },
    { kind: 'input', name: 'Ru', label: 'Ultimate Resistance', unit: 'kips', helpText: 'Enter the ultimate resistance', placeholder: 'Ultimate Resistance' },
    { kind: 'input', name: 'Xe', label: 'Elastic Displacement', unit: 'in', helpText: 'Enter the elastic displacement', placeholder: 'Elastic Displacement' },
    { kind: 'dropdown', name: 'loadType', label: 'Load Type', options: LOAD_TYPES, helpText: 'Select the load type...' },
    { kind: 'input', name: 'Pa', label: 'Pressure Values', unit: 'psi', helpText: 'Comma-separated pressure values', placeholder: '5.0, 10.0' },
    { kind: 'input', name: 'Ia', label: 'Impulse Values', unit: 'psi-msec', helpText: 'Comma-separated impulse values', placeholder: '20.0, 40.0' },
    { kind: 'input', name: 'ductilityLimit', label: 'Ductility Limit', unit: '', helpText: 'Comma-separated ductility values', placeholder: '1, 2, 5, 10' },
    { kind: 'input', name: 'rotationLimit', label: 'Rotation Limit', unit: 'deg', helpText: 'Comma-separated rotation values', placeholder: '2, 4, 6, 12' }
  ]);
  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">PI Diagram Analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid grid-rows-[auto_1fr] rounded-xl gap-y-0 w-fit">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            {/* The PI Diagram response goes here  */}
            <ResponseDisplay data={analysisResult} sections={RESPONSE_DISPLAY_CONFIG} emptyMessage="No analysis results yet." />
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.charts && (
              <div className="grid grid-cols-[3fr_1fr] gap-2 m-5 print:gap-1">
                {Object.entries(analysisResult.charts)
                  .filter(([, v]) => !!v)
                  .map(([key, v]) => {
                    const chart = v!;
                    const src = `data:${chart.mime};base64,${chart.base64}`;

                    return (
                      <div key={key} className="analysis-app__chart-card break-inside-avoid">
                        <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div className="hidden print:block print-root ">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex h-full min-h-0 flex-col">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iPI Diagram'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>

                  <div className="m-0 p-0 ">
                    <table className="w-full table-fixed text-[10px] m-0 p-0">
                      <colgroup>
                        <col className="w-55%]" />
                        <col className="w-[45%]" />
                      </colgroup>

                      <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Inputs</caption>

                      <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                        {(
                          [
                            ['Length', `${values.length} ft`],
                            ['Tributary Width', `${values.tribWidth} ft`],
                            ['Hinge Length', `${values.hingeLength} ft`],
                            ['Total Weight', `${values.totalWeight} psf`],
                            ['KLM Factor', values.klmFactor],
                            ['Ultimate Resistance', `${values.Ru} kips`],
                            ['Elastic Displacement', `${values.Xe} in`],
                            ['Load Type', LOAD_TYPES.find(opt => opt.value === values.loadType)?.label || '—'],
                            ['Pressure Values', `${values.Pa} psi`],
                            ['Impulse Values', `${values.Ia} psi-ms`],
                            ['Ductility Limit', values.ductilityLimit],
                            ['Rotation Limit', `${values.rotationLimit} deg`]
                          ] as Array<[React.ReactNode, React.ReactNode]>
                        ).map(([label, value], i) => (
                          <tr key={i}>
                            <td className="font-semibold">{label}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    {analysisResult && (
                      <table className="w-full table-fixed text-[10px] m-0 p-0">
                        <colgroup>
                          <col className="w-[55%]" />
                          <col className="w-[45%]" />
                        </colgroup>

                        <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Results</caption>

                        <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                          {[
                            ['Tributary Area', `${analysisResult.TA} ft²`],
                            ['Stiffness', `${analysisResult.K} kips/in`],
                            ['Effective Mass', `${analysisResult.Me} lbf-in/sec²`],
                            ['Natural Period', `${analysisResult.Tn} ms`],
                            ['Ultimate Resistance', `${analysisResult.Ru} psi`]
                          ].map(([label, value], i) => (
                            <tr key={i}>
                              <td className="font-semibold">{label}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 pl-2 overflow-hidden">
                  {analysisResult?.charts && (
                    <div className="grid h-full min-h-0 grid-cols-1 gap-0 ap-0 overflow-hidden border-0 p-0 box-border">
                      {Object.entries(analysisResult.charts)
                        .filter(([, v]) => !!v)
                        .map(([key, v]) => {
                          const chart = v!;
                          const src = `data:${chart.mime};base64,${chart.base64}`;

                          return (
                            <div key={key} className="h-full min-h-0 min-w-0 overflow-hidden break-inside-avoid">
                              <img src={src} alt={`${key} chart`} loading="lazy" className="block h-full w-full max-h-full max-w-full object-contain rounded-none" />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
